import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Spinner, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';
import '../ChatApp.css';

// ─── Constants ──────────────────────────────────────────────────────────────

const VARIATION_PROMPTS = {
  multi_step:
    'Convert the form to a multi-step form so that it improves form fill rates. ' +
    'Name and Phone number can be in step 1, the rest in step 2. ' +
    'Use a conversion focused CTA.',
  sticky:
    'Create a sticky form with messaging then CTAs based on whatever available assets — ' +
    'Phone Number, WhatsApp, Get Directions. Keep the form compact and pinned so it never ' +
    'loses visibility on scroll.',
  multi_step_messaging:
    'Convert the form to a multi-step form with key reinforcement messaging (headline and ' +
    'sub-headline) at the top so that it improves form fill rates. Name and Phone number can ' +
    'be in step 1, the rest in step 2. Use a conversion focused CTA.',
  long_with_captcha:
    'Use the same form with captchas. Captchas don\'t have to come from the server — build ' +
    'them in the code: pick 8–10 pre-decided captcha images and values, and rotate them ' +
    'randomly for each user to validate.',
  better_design:
    'Provide a better designed form. Keep the field set the same, improve hierarchy, ' +
    'spacing, labels, micro-copy, error states, and CTA emphasis. Make it mobile-first.',
};

const VARIATION_OPTIONS = [
  { value: 'multi_step',           label: 'Multi-Step Form' },
  { value: 'sticky',               label: 'Sticky Form' },
  { value: 'multi_step_messaging', label: 'Multi-Step Form with Key Messaging' },
  { value: 'long_with_captcha',    label: 'Long Form with Captcha' },
  { value: 'better_design',        label: 'Better Designed Form' },
];

const LLM_PROVIDERS = {
  openai:    { label: 'OpenAI',           models: [
    { value: 'gpt-4.1',                    label: 'GPT-4.1 (latest)' },
    { value: 'gpt-4o',                     label: 'GPT-4o' },
    { value: 'gpt-4o-mini',                label: 'GPT-4o Mini' },
  ]},
  anthropic: { label: 'Anthropic Claude', models: [
    { value: 'claude-opus-4-7',            label: 'Claude Opus 4.7 (latest)' },
    { value: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6' },
    { value: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku 4.5' },
  ]},
  gemini:    { label: 'Google Gemini',    models: [
    { value: 'gemini-3.1-pro-preview',     label: 'Gemini 3.1 Pro (latest)' },
    { value: 'gemini-2.5-pro',             label: 'Gemini 2.5 Pro' },
    { value: 'gemini-1.5-pro',             label: 'Gemini 1.5 Pro' },
  ]},
};

const FONT_FACE_STYLE_RE = /<style[^>]*\bid\s*=\s*["']sxi_[^"']+_font_face["'][^>]*>[\s\S]*?<\/style>/gi;

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractFontFaceStyles(html) {
  const safe = html || '';
  const fontFaceBlocks = safe.match(FONT_FACE_STYLE_RE) || [];
  const stripped = safe.replace(FONT_FACE_STYLE_RE, '');
  return { stripped, fontFaceBlocks };
}

function buildVariationSrcDoc({ html, css, js, fontFaceBlocks, sectionName }) {
  const ff = (fontFaceBlocks || []).join('\n');
  const safeTitle = String(sectionName || 'Form Variation').replace(/[<>&"]/g, '');
  return (
    `<!DOCTYPE html><html lang="en"><head>` +
    `<meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<title>${safeTitle}</title>` +
    ff +
    `<style>${css || ''}</style>` +
    `</head><body style="margin:0;padding:0">${html || ''}` +
    (js ? `<script>${js}<\/script>` : '') +
    `</body></html>`
  );
}

async function pollGenerationJob(app_url, jobId) {
  for (let i = 0; i < 300; i++) {
    let job;
    try {
      const res = await axios.get(`${app_url}/api/ai_generation/job/${jobId}`, { withCredentials: true });
      job = res.data?.job;
    } catch (err) {
      if (i > 2) throw new Error('Lost connection to job status');
      await new Promise(r => setTimeout(r, 2000));
      continue;
    }
    if (!job) throw new Error('Job not found');
    if (job.status === 'succeeded') return job.result || {};
    if (job.status === 'failed') throw new Error(job.error?.message || 'Generation failed');
    await new Promise(r => setTimeout(r, 2000));
  }
  throw new Error('Generation timed out');
}

function formLabel(form) {
  if (form?.name) return form.name;
  const u = form?.original_url || form?.imported_url || '';
  return u ? u.replace(/^https?:\/\//, '').slice(0, 60) : 'Form';
}

// ─── Component ──────────────────────────────────────────────────────────────

const Forms = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { if (ViewedPage) ViewedPage(60); }, []);

  // Loaders
  const [loading, setLoading] = useState(true);

  // Data
  const [forms, setForms] = useState([]);
  const [assets, setAssets] = useState([]);

  // Picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [variation, setVariation] = useState('multi_step');
  const [llmProvider, setLlmProvider] = useState('openai');
  const [llmModel, setLlmModel] = useState('gpt-4.1');

  // Generation
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]); // [{ variant_label, source_label, section, fontFaceBlocks }]

  // ─── Load on mount ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    (async () => {
      try {
        const [formsRes, assetsRes] = await Promise.all([
          axios.post(`${app_url}/api/firebase/get_imported_forms`, {}, { withCredentials: true }),
          axios.post(`${app_url}/api/assets/get_all`, { shop_domain }, { withCredentials: true }),
        ]);
        if (formsRes.data?.success) setForms(formsRes.data.data || []);
        if (assetsRes.data?.success) setAssets(assetsRes.data.data || []);
      } catch (err) {
        console.error('Forms load failed', err);
        toast.error('Failed to load Forms data');
      } finally {
        setLoading(false);
      }
    })();
  }, [shop_domain, app_url]);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const contactAssets = useMemo(() => assets.filter(a => a.type === 'Contact'), [assets]);
  const directionAssets = useMemo(() => assets.filter(a => a.type === 'Get Directions'), [assets]);

  const stickyBusinessContext = useMemo(() => {
    const lines = [];
    for (const a of contactAssets) {
      // New fields take precedence; fall back to legacy `url` + `dial_code` so
      // older Contact rows still render the same line they did before.
      const phoneDial = a.phone_dial_code || a.dial_code || '';
      const phoneNum  = a.phone_number    || a.url       || '';
      if (phoneNum) {
        const prefix = phoneDial ? `${phoneDial} ` : '';
        lines.push(`- Phone (${a.title || 'Contact'}): ${prefix}${phoneNum}`);
      }

      const waDial = a.whatsapp_dial_code || a.dial_code || '';
      const waNum  = a.whatsapp_number    || a.url       || '';
      const waDigits = `${waDial.replace(/\D/g, '')}${waNum.replace(/\D/g, '')}`;
      if (waDigits) lines.push(`- WhatsApp link (${a.title || 'Contact'}): https://wa.me/${waDigits}`);
    }
    for (const a of directionAssets) {
      lines.push(`- Get Directions (${a.title || 'Address'}): https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.url || '')}`);
    }
    return lines.length
      ? `Use these available business contacts when building the sticky form's CTAs:\n${lines.join('\n')}`
      : '';
  }, [contactAssets, directionAssets]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleProviderChange = (e) => {
    const p = e.target.value;
    setLlmProvider(p);
    setLlmModel(LLM_PROVIDERS[p].models[0].value);
  };

  const handleClear = () => setResults([]);

  const handleGenerate = async () => {
    if (!selectedFormId) { toast.error('Pick a form first'); return; }
    const form = forms.find(f => f.id === selectedFormId);
    if (!form) { toast.error('Selected form is no longer available'); return; }
    if (!form.imported_url) { toast.error('Selected form has no imported_url'); return; }

    setGenerating(true);
    try {
      // 1) Fetch form HTML
      const htmlRes = await axios.post(
        `${app_url}/api/firebase/get_section_html`,
        { url: form.imported_url },
        { withCredentials: true }
      );
      const originalHtml = htmlRes.data?.data || '';
      if (!originalHtml) throw new Error('Could not fetch form HTML');

      // 2) Strip font-face
      const { stripped, fontFaceBlocks } = extractFontFaceStyles(originalHtml);

      // 3) Build payload
      const userMessage = VARIATION_PROMPTS[variation];
      const selectedAssets = (variation === 'sticky' && stickyBusinessContext)
        ? { businessContext: stickyBusinessContext }
        : {};

      const sectionId = form.id;
      const sectionName = formLabel(form);

      // 4) Kick off generation
      const startRes = await axios.post(
        `${app_url}/api/ai_generation/generate_sections`,
        {
          page: form.original_url || form.imported_url,
          outputType: 'sections',
          mode: 'improve',
          selectedAssets,
          selectedSection: {
            section_id:   sectionId,
            section_name: sectionName,
            url:          form.imported_url,
            htmlContent:  stripped,
          },
          userMessage,
          llmProvider,
          llmModel,
          sessionCacheId: null,
          chatContext: { lastExchange: null, summary: '' },
          jumpLinks: [],
        },
        { withCredentials: true }
      );
      if (!startRes.data?.success || !startRes.data?.jobId) {
        throw new Error(startRes.data?.message || 'Server did not return a jobId');
      }

      // 5) Poll
      const result = await pollGenerationJob(app_url, startRes.data.jobId);
      const sections = Array.isArray(result?.sections) ? result.sections : [];
      if (sections.length === 0) {
        toast.info('No variation returned. Try a different combination.');
        return;
      }

      const variantLabel = (VARIATION_OPTIONS.find(o => o.value === variation) || {}).label || variation;
      const newCards = sections.map(s => ({
        section: s,
        fontFaceBlocks,
        variant_label: variantLabel,
        source_label: sectionName,
      }));
      setResults(prev => [...prev, ...newCards]);
      toast.success(`Generated ${variantLabel}`);
      setPickerOpen(false);
    } catch (err) {
      console.error('Forms generate error', err);
      toast.error(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='main-container' id='forms-main-container'>
        <div className='main_conatiner_spinner'><Spinner animation='border' /></div>
      </div>
    );
  }

  return (
    <div className='main-container' id='forms-main-container'>
      <div
        className='main-title mt-4 mx-4 d-flex justify-content-between align-items-center'
        style={{
          marginBottom: '10px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '16px 20px',
          backgroundColor: '#ffffff',
        }}
      >
        <div className='d-flex align-items-center gap-2'>
          <h2 style={{ margin: 0, fontWeight: 'bold' }}>Forms</h2>
          {results.length > 0 && (
            <span className='campaign-tag-new'>{results.length}</span>
          )}
          {results.length > 0 && (
            <button
              type='button'
              onClick={handleClear}
              className='btn btn-link btn-sm p-0 ms-1'
              style={{ fontSize: 12, color: '#6c757d', textDecoration: 'underline' }}
              disabled={generating}
            >
              Clear
            </button>
          )}
        </div>
        <Button
          className='sxi_primary_blue_btn'
          onClick={() => setPickerOpen(true)}
          disabled={generating}
          style={{ padding: '8px 16px' }}
        >
          {generating
            ? <><Spinner size='sm' animation='border' /> Generating…</>
            : 'Select & Generate'}
        </Button>
      </div>

      {results.length === 0 && !generating && (
        <div
          className='mx-4 p-4 text-center'
          style={{ border: '1px solid #e9ecef', borderRadius: 8, backgroundColor: '#ffffff', color: '#6B7280' }}
        >
          {forms.length === 0
            ? <>No imported forms yet. <strong style={{ color: 'var(--cux-ink, #132848)' }}>imported_forms</strong> on the store doc is empty — populate it via the form-import flow first.</>
            : <>No variations yet. Click <strong style={{ color: 'var(--cux-ink, #132848)' }}>Select &amp; Generate</strong> to create your first one.</>
          }
        </div>
      )}

      {results.length > 0 && (
        <div className='mx-3 p-2'>
          {results.map((entry, idx) => (
            <div
              key={`${idx}-${entry.variant_label}`}
              className='sxi-existing-div-wrapper'
              style={{
                marginBottom: '1rem',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '10px',
                backgroundColor: '#ffffff',
              }}
            >
              <div style={{
                padding: '8px 14px',
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
              }}>
                <strong style={{ color: 'rgb(5,44,101)' }}>{entry.source_label}</strong>
                <span className='campaign-tag-new ms-1'>{entry.variant_label}</span>
                {entry.section?.firebaseUrl && (
                  <a
                    href={entry.section.firebaseUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='btn btn-sm btn-outline-secondary ms-auto'
                    style={{ fontSize: 12 }}
                  >
                    Open URL
                  </a>
                )}
              </div>
              <iframe
                srcDoc={buildVariationSrcDoc({
                  html: entry.section?.html,
                  css: entry.section?.css,
                  js: entry.section?.js,
                  fontFaceBlocks: entry.fontFaceBlocks,
                  sectionName: entry.section?.section_name || entry.source_label,
                })}
                style={{ width: '100%', height: 560, border: 'none', display: 'block', background: '#ffffff' }}
                sandbox='allow-scripts allow-same-origin'
                title={`${entry.source_label} — ${entry.variant_label}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Picker modal */}
      <Modal
        show={pickerOpen}
        onHide={() => !generating && setPickerOpen(false)}
        size='lg'
        centered
        scrollable
      >
        <Modal.Header closeButton={!generating}>
          <Modal.Title>Pick a Form &amp; Variation</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className='mb-3' controlId='forms-form-select'>
            <Form.Label className='mb-1'>Form</Form.Label>
            <Form.Select
              className='form-select form-select-sm'
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
              disabled={generating || forms.length === 0}
              style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 12px', fontSize: 14 }}
            >
              <option value=''>{forms.length === 0 ? 'No imported forms available' : 'Select a form…'}</option>
              {forms.map(f => (
                <option key={f.id} value={f.id}>{formLabel(f)}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className='mb-3' controlId='forms-variation-select'>
            <Form.Label className='mb-1'>Variation Type</Form.Label>
            <Form.Select
              className='form-select form-select-sm'
              value={variation}
              onChange={(e) => setVariation(e.target.value)}
              disabled={generating}
              style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 12px', fontSize: 14 }}
            >
              {VARIATION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
            {variation === 'sticky' && (
              <Form.Text className='text-muted'>
                {contactAssets.length + directionAssets.length === 0
                  ? 'No Contact or Get Directions assets found — the AI will improvise CTAs.'
                  : `Using ${contactAssets.length} contact${contactAssets.length !== 1 ? 's' : ''} and ${directionAssets.length} direction asset${directionAssets.length !== 1 ? 's' : ''} from this store.`}
              </Form.Text>
            )}
          </Form.Group>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label className='mb-1'>AI Provider</Form.Label>
              <Form.Select
                className='form-select form-select-sm'
                value={llmProvider}
                onChange={handleProviderChange}
                disabled={generating}
              >
                {Object.entries(LLM_PROVIDERS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className='mb-1'>Model</Form.Label>
              <Form.Select
                className='form-select form-select-sm'
                value={llmModel}
                onChange={(e) => setLlmModel(e.target.value)}
                disabled={generating}
              >
                {LLM_PROVIDERS[llmProvider].models.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className='sxi_primary_blue_btn'
            onClick={handleGenerate}
            disabled={!selectedFormId || generating}
            style={{ padding: '8px 16px' }}
          >
            {generating
              ? <><Spinner size='sm' animation='border' /> Generating…</>
              : 'Generate'}
          </Button>
          <button className='back-button' onClick={() => setPickerOpen(false)} disabled={generating}>Cancel</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Forms;
